/**
 * ARENA MENU — the front end, rebuilt from scratch.
 * (Owner, 2026-08-09: "redesign the game menu fully from scratch, don't use the
 * current UI at all, design how a FIFA game menu looks, easy to navigate".)
 *
 * Shares no markup, no CSS and no helpers with ui/menu.ts. It implements the
 * SAME `TitleFlowHandlers` contract, so main.ts swaps one mount call for the
 * other and every downstream path — practice, online, settings — is untouched.
 *
 * ── The navigation model, which is the whole point ──────────────────────────
 *
 * One idea: **focus is a value in a model, not a DOM :focus.** Every screen is
 * `{ items[], focus }`, arrows move `focus`, Enter runs `items[focus].act()`,
 * Esc pops the stack. Mouse hover writes the same `focus`, and click runs the
 * same `act`.
 *
 * That is what makes it feel like a console menu rather than a web page: there
 * is exactly one highlighted thing at all times, the keyboard and the mouse can
 * never disagree about what it is, and adding a gamepad later means mapping a
 * stick onto `move()` and nothing else.
 *
 * The screen STACK is what makes Esc trustworthy: every push records where you
 * came from, so Back always means back, and there is no screen you can enter
 * and not leave. The old flow had several dead ends.
 *
 * ── Why the button-prompt strip exists ──────────────────────────────────────
 * The bottom bar always states what Enter and Esc do RIGHT NOW. It is the
 * cheapest possible fix for "easy to navigate": nobody has to discover the
 * controls, because they are on screen permanently, and it costs one row of
 * pixels. Every console game does this; almost no web menu does.
 */

import type { SquadSize } from '@fg/shared/sim';
import type { Difficulty } from '@fg/shared/ai';
import type { Session } from '@supabase/supabase-js';
import { deriveKit } from '../render/kit-colors.js';
import {
  DEFAULT_DISPLAY_NAME,
  loadProfile,
  sanitiseDisplayName,
  updateProfile,
} from './profile-store.js';
import {
  accountsEnabled,
  getLeaderboard,
  getMatchHistory,
  getProfile as getAccountProfile,
  onAuthChange,
  saveProfile as saveAccountProfile,
  signInWithGoogle,
  signOut as signOutAccount,
  type AccountProfile,
  type LeaderboardEntry,
  type MatchHistoryEntry,
} from '../account.js';
import { lobbyUrl } from '../config.js';
import type { GameSettings, TimeOfDay, Weather } from './settings.js';
import { CLUBS, NATIONS, teamOrStub } from './teams.js';
import {
  advanceWorldCupRound, careerMatchToken, careerSeasonComplete,
  careerTable, careerTotalRounds, clearCareer, clearWorldCup, groupOf,
  groupStandings, loadCareer, loadWorldCup, nextCareerFixture, nextWorldCupMatch,
  saveCareer, saveWorldCup, startCareer, startNextSeason, startWorldCup,
  worldCupEliminated, worldCupStage, loadHonours, recordHonours,
  recordWorldCupHonours, recordSeasonHonours,
  type CompetitionContext, type WorldCupState,
} from './tournament-store.js';
import type { MatchSetup, RoomSquadSize, StartScreen, TitleFlow, TitleFlowHandlers } from './menu.js';
import { awardKnockout, awardSeason } from '../economy/award.js';
import { balance } from '../economy/wallet.js';
import { formatCash, formatCoins, itemsOfKind } from '../economy/catalogue.js';
import {
  entitlements, equipCelebration, equippedCelebration, paymentAvailable,
} from '../economy/entitlements.js';
import type { CatalogueItem } from '../economy/types.js';
import './arena-menu.css';

/** A focusable thing on a screen. */
interface Item {
  /** Big label. */
  name: string;
  /** Small label above the name (tiles only). */
  kicker?: string;
  /** One line under the name (tiles only). */
  sub?: string;
  /** Sentence shown in the description pane while focused. A function form
   *  re-reads on every render, for copy that depends on the current option. */
  desc?: string | (() => string);
  disabled?: boolean;
  act?(): void;
  /** Present on option rows: left/right cycles a value. */
  cycle?(dir: -1 | 1): void;
  /** Renders the row's options when it is an option row. */
  options?(): { label: string; selected: boolean }[];
  label?: string;
  /** Tile only: draw this team's flag/crest on the card. */
  badgeTeamId?: string;
  /**
   * Tile only: a photo behind the card, under the existing glass tint and a
   * bottom-heavy scrim (applied in render() — see its comment there). Kept
   * OFF badged tiles (club/nation pickers) on purpose: those already carry a
   * flag/crest as their identity mark, and a second image behind a 260px card
   * competes with the badge rather than adding anything a photo-less card at
   * this size can't already show.
   */
  art?: string;
  /** Store rows only. */
  storeItem?: import('../economy/types.js').CatalogueItem;
  storeState?: 'equipped' | 'owned' | 'afford' | 'short';
}

/** Resolve an item's description whether it was given as text or a thunk. */
function descOf(it: Item | undefined): string {
  const d = it?.desc;
  return typeof d === 'function' ? d() : (d ?? '');
}

interface Screen {
  id: string;
  /** Shown above the content. */
  heading: string;
  /**
   * 'tiles' = card row, 'rows' = option list, 'name' = text field,
   * 'table'  = a standings/bracket panel with a focusable action list beside it.
   */
  kind: 'tiles' | 'rows' | 'name' | 'table' | 'moment' | 'store';
  /** 'moment' only: the big result card. */
  moment?: {
    tone: 'win' | 'loss';
    kicker: string;
    headline: string;
    line: string;
    /** The encouraging bit. A loss screen that only states the loss is a wall. */
    note: string;
    teamId?: string;
  };
  /** For 'table': the panel's rendered content, rebuilt on every render. */
  panel?(): HTMLElement;
  items: Item[];
  /** Which top tab is highlighted while this screen is up. */
  tab: string;
  /** Enter's verb on the prompt strip. */
  enterLabel?: string;
  /** Esc's verb; absent on the root screen, which cannot be backed out of. */
  backLabel?: string;
}

/*
 * WORLD CUP is its own section rather than a tile inside CAREER.
 *
 * They are two unrelated commitments — a league season is a long grind you
 * return to, a tournament is a short run you restart — and burying one behind
 * the other cost a click on the mode people dip into most. Real football front
 * ends give a tournament its own door for the same reason.
 */
const TABS = ['PLAY', 'CAREER', 'WORLD CUP', 'STORE', 'ONLINE', 'PROFILE', 'SETTINGS'] as const;

/** Match-history result glyphs. Word + symbol, so it reads without colour. */
const RESULT_LABEL: Record<MatchHistoryEntry['result'], string> = {
  W: 'WON  ▲',
  L: 'LOST ▼',
  D: 'DREW ■',
};

/**
 * A played_at timestamp as a short local date. Falls back to the raw string if
 * Postgres ever hands back something Date cannot parse — a history row with an
 * odd date is still worth showing.
 */
function formatMatchDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
/*
 * Tab cycles SECTIONS only. SETTINGS is an ACTION — it opens a modal over the
 * menu — and cycling into it fires that modal mid-keystroke, which is a nasty
 * surprise when you were only paging through sections. It stays clickable and
 * stays in the rail; it just is not a stop on the Tab circuit.
 */
const CYCLE_TABS = TABS.filter((t) => t !== 'SETTINGS');

const SQUADS: readonly SquadSize[] = [2, 3, 5, 7, 11];
const DIFFICULTIES: readonly Difficulty[] = ['easy', 'normal', 'hard'];
const HALVES: readonly number[] = [2, 3, 5];
const TIMES: readonly TimeOfDay[] = ['day', 'golden', 'overcast', 'night'];
const WEATHERS: readonly Weather[] = ['clear', 'rain'];

const TIME_LABEL: Record<TimeOfDay, string> = {
  day: 'Day', golden: 'Golden Hour', overcast: 'Overcast', night: 'Night',
};
const WEATHER_LABEL: Record<Weather, string> = { clear: 'Clear', rain: 'Rain' };

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

function linearToHex(rgb: readonly [number, number, number]): string {
  const conv = (c: number): number => {
    const s = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(s * 255)));
  };
  return `#${((1 << 24) | (conv(rgb[0]) << 16) | (conv(rgb[1]) << 8) | conv(rgb[2])).toString(16).slice(1)}`;
}

export function mountArenaMenu(
  container: HTMLElement,
  _settings: GameSettings,
  cb: TitleFlowHandlers,
  startAt: StartScreen = 'title',
): TitleFlow {
  // ── State ────────────────────────────────────────────────────────────────
  const setup: MatchSetup = {
    squad: 5,
    difficulty: 'normal',
    halfMinutes: 3,
    timeOfDay: 'day',
    weather: 'clear',
  };
  let playerName = sanitiseDisplayName(loadProfile().displayName || DEFAULT_DISPLAY_NAME);
  let joinCode = '';

  // ── Account (Supabase Auth + profile row) ───────────────────────────────
  // Absent VITE_SUPABASE_* env vars mean accountsEnabled() is false and every
  // item below is simply never pushed onto the PROFILE screen — guests keep
  // the exact experience they had before this feature existed.
  const AVATAR_COUNT = 12;
  let accountSession: Session | null = null;
  let accountProfile: AccountProfile | null = null;
  /**
   * The in-flight profile fetch, so a click that lands in the ~half-second
   * between "session known" and "profile arrived" can WAIT for the answer
   * instead of concluding "guest" and asking a signed-in player their name —
   * the exact prompt withPlayerName exists to remove. Null once settled.
   */
  let accountProfileLoading: Promise<AccountProfile | null> | null = null;
  let avatarDraft = 0;
  /**
   * Phase-2 match history. `null` means "not fetched yet" and renders as a
   * loading row — distinct from `[]`, which means the fetch came back and this
   * player genuinely has no finished matches. Collapsing the two would show
   * "no matches yet" for a moment to someone who has plenty.
   */
  let accountHistory: MatchHistoryEntry[] | null = null;
  /**
   * `null` = not fetched yet (renders nothing rather than a misleading "0"
   * before the first response lands). Polled — see the interval set up near
   * the auth watcher below — because it is genuinely live state, not
   * something an auth or screen-navigation event would ever refresh.
   */
  let activePlayers: number | null = null;

  const stack: Screen[] = [];
  /*
   * Transient confirmation ("X unlocked"), shown in the prompt strip rather
   * than a modal — feedback belongs where the player is already looking, and a
   * purchase should not need dismissing.
   */
  let flashText = '';
  let flashUntil = 0;
  const flash = (t: string): void => {
    flashText = t;
    flashUntil = performance.now() + 2600;
  };
  let focus = 0;
  /*
   * Owner: "it is highlighted even when mouse is removed from it fix this
   * issue". This menu is keyboard-first by design (see the file header) —
   * `focus` is one shared cursor so arrow keys, mouse and a future gamepad
   * all move the SAME thing, and a keyboard user needs SOMETHING highlighted
   * at all times, because there is no DOM :focus to fall back to. A mouse
   * user does not: real hover clears the moment the pointer leaves, and the
   * bug was that a hover-driven `focus` stayed lit exactly like a keyboard
   * one would, because nothing distinguished the two.
   *
   * `focusSource` is that distinction. 'keyboard' (the default, and what
   * every screen navigation resets to below) means "always show it" — arrow
   * keys have no pointer position to fall back on, and a fresh screen's first
   * item should read as selected the instant it appears. 'mouse' means "show
   * it only while the pointer is actually over the grid" — `mouseInsideGrid`
   * is that second half, flipped on by `setFocus`'s own mouse path and off by
   * the single `mouseleave` on `contentEl` below. Two flags, not one boolean,
   * because a keyboard press while the mouse happens to be resting outside
   * the grid must still show its target — that state does not fit into
   * "on while hovering, off otherwise" alone.
   */
  let focusSource: 'keyboard' | 'mouse' = 'keyboard';
  let mouseInsideGrid = false;
  const focusVisible = (i: number): boolean =>
    i === focus && (focusSource === 'keyboard' || mouseInsideGrid);
  let disposed = false;

  // ── DOM shell, built once ────────────────────────────────────────────────
  const root = el('div', 'am-root');
  /*
   * THE BACKGROUND ART LAYER. One div, one CSS `background-image`, swapped by
   * `data-tab` (set in render()) rather than four separate elements — a
   * section's identity already lives in `s.tab`, so this reuses that instead
   * of tracking a second "which art is showing" value that could disagree
   * with it. Sits between the base gradient (`.am-root::before`) and the rail/
   * body/prompts in DOM order, which is what puts it behind everything without
   * needing an explicit z-index. Owner-supplied art (2026-08-14): each image
   * is pre-composed dark-left/bright-right, matching this layout's content-
   * on-the-left shape, and `.am-art::after`'s scrim gradient doubles that
   * margin so body text never depends on an image happening to be dark enough
   * under it.
   */
  const art = el('div', 'am-art');
  const rail = el('div', 'am-rail');
  const brand = el('div', 'am-brand');
  const brandLogo = el('img', 'am-brand-logo');
  brandLogo.src = '/ui/brand/logo.webp';
  brandLogo.alt = 'Footy Arena';
  brand.appendChild(brandLogo);
  const tabsEl = el('div', 'am-tabs');
  const who = el('div', 'am-who');
  rail.append(brand, tabsEl, who);

  const body = el('div', 'am-body');
  const headingEl = el('p', 'am-heading');
  const contentEl = el('div', '');
  const descEl = el('div', 'am-desc');
  body.append(headingEl, contentEl, descEl);

  const prompts = el('div', 'am-prompts');
  const statusEl = el('div', 'am-status');

  root.append(art, rail, body, prompts);
  container.appendChild(root);

  const kitA = linearToHex(deriveKit(0).primary);

  // ── Screen builders ──────────────────────────────────────────────────────
  // A fresh screen's first item reads as selected immediately, regardless of
  // where the mouse happens to be resting — the same reason `focusSource`
  // defaults to 'keyboard' above.
  const push = (s: Screen): void => { stack.push(s); focus = 0; focusSource = 'keyboard'; render(); };
  const pop = (): void => {
    if (stack.length > 1) { stack.pop(); focus = 0; focusSource = 'keyboard'; render(); }
  };
  const replaceRoot = (s: Screen): void => {
    stack.length = 0; stack.push(s); focus = 0; focusSource = 'keyboard'; render();
  };
  /*
   * Rebuild the CURRENT screen from its builder, keeping the cursor where it is.
   *
   * A screen captures its items at construction time, so anything that changes
   * state behind them — buying, equipping — leaves stale rows. The store showed
   * this plainly: buy a celebration and its button still read "Buy" until you
   * navigated away and back, which in a shop is exactly the bug that makes a
   * player wonder whether their coins actually went anywhere. replaceRoot would
   * fix the staleness but throw the player back to the first row.
   */
  const refreshTop = (build: () => Screen): void => {
    const keep = focus;
    stack[stack.length - 1] = build();
    focus = Math.min(keep, Math.max(0, (stack[stack.length - 1]?.items.length ?? 1) - 1));
    render();
  };

  /**
   * Account state changes asynchronously (auth events, profile fetches) and
   * must never yank a player off whatever screen they are actually on — so
   * this only rebuilds when PROFILE happens to be the visible screen.
   */
  const refreshProfileIfVisible = (): void => {
    if (stack[stack.length - 1]?.id === 'profile') refreshTop(profileScreen);
  };

  const matchSetupScreen = (): Screen => ({
    id: 'setup',
    tab: 'PLAY',
    heading: 'Match Setup',
    kind: 'rows',
    enterLabel: 'Kick Off',
    backLabel: 'Back',
    items: [
      {
        name: 'Squad Size', label: 'Squad Size',
        desc: 'How many players a side. Smaller squads play on a smaller pitch.',
        options: () => SQUADS.map((v) => ({ label: `${v}v${v}`, selected: v === setup.squad })),
        cycle: (d) => {
          const i = SQUADS.indexOf(setup.squad);
          setup.squad = SQUADS[(i + d + SQUADS.length) % SQUADS.length]!;
        },
      },
      {
        name: 'Difficulty', label: 'Difficulty',
        desc: 'The COM only gets smarter, never faster — reaction time, options read, and error, never stats.',
        options: () => DIFFICULTIES.map((v) => ({ label: v, selected: v === setup.difficulty })),
        cycle: (d) => {
          const i = DIFFICULTIES.indexOf(setup.difficulty);
          setup.difficulty = DIFFICULTIES[(i + d + DIFFICULTIES.length) % DIFFICULTIES.length]!;
        },
      },
      {
        name: 'Half Length', label: 'Half Length',
        desc: 'Minutes per half.',
        options: () => HALVES.map((v) => ({ label: `${v} min`, selected: v === setup.halfMinutes })),
        cycle: (d) => {
          const i = HALVES.indexOf(setup.halfMinutes);
          setup.halfMinutes = HALVES[(i + d + HALVES.length) % HALVES.length]!;
        },
      },
      {
        name: 'Time of Day', label: 'Time of Day',
        desc: 'Night matches light the stadium — and goals set off fireworks.',
        options: () => TIMES.map((v) => ({ label: TIME_LABEL[v], selected: v === setup.timeOfDay })),
        cycle: (d) => {
          const i = TIMES.indexOf(setup.timeOfDay);
          setup.timeOfDay = TIMES[(i + d + TIMES.length) % TIMES.length]!;
        },
      },
      {
        name: 'Weather', label: 'Weather',
        desc: 'Rain wets the pitch: the ball runs on, and turns take longer.',
        options: () => WEATHERS.map((v) => ({ label: WEATHER_LABEL[v], selected: v === setup.weather })),
        cycle: (d) => {
          const i = WEATHERS.indexOf(setup.weather);
          setup.weather = WEATHERS[(i + d + WEATHERS.length) % WEATHERS.length]!;
        },
      },
      {
        name: 'Kick Off',
        desc: 'Start the match with the settings above.',
        act: () => { cb.persist(); cb.onStartPractice({ ...setup }); },
      },
    ],
  });

  const nameScreen = (then: (name: string) => void, heading: string): Screen => ({
    id: 'name',
    tab: 'ONLINE',
    heading,
    kind: 'name',
    enterLabel: 'Confirm',
    backLabel: 'Back',
    items: [{ name: 'name', desc: 'This is what other players see. Up to 16 characters.' }],
    // `act` is wired by the renderer, which owns the input element.
    ...({ onConfirm: then } as object),
  });

  /**
   * THE ONE WAY INTO AN ONLINE ACTION THAT NEEDS A NAME.
   *
   * A signed-in player with a username IS that username — asking them to type
   * a display name on every Quick Match / Create Room / Ranked was the "why is
   * it asking my name for all modes?" complaint (owner, 2026-08-16; CR-10).
   * They go straight through. A guest gets the prompt, pre-filled with the
   * last name they used — and what they type is REMEMBERED (`playerName`), so
   * the name given for Quick Match is the one offered for Create Room next.
   * `nameScreen`'s own onConfirm never wrote it back, so every mode started
   * from the stored profile again.
   */
  const withPlayerName = (then: (name: string) => void): void => {
    const go = (): void => {
      const signedIn = accountProfile?.username;
      if (signedIn) { cb.persist(); then(signedIn); return; }
      push(nameScreen((n) => {
        playerName = sanitiseDisplayName(n) || DEFAULT_DISPLAY_NAME;
        cb.persist();
        then(playerName);
      }, 'Your name'));
    };
    // Signed in but the profile is still on its way: wait for it rather than
    // deciding "guest" and prompting. A player without a username row still
    // gets the prompt — correctly — once the fetch settles.
    if (accountSession && !accountProfile && accountProfileLoading) {
      void accountProfileLoading.then(go, go);
      return;
    }
    go();
  };

  const playScreen = (): Screen => ({
    id: 'play',
    tab: 'PLAY',
    heading: 'Choose a mode',
    kind: 'tiles',
    enterLabel: 'Select',
    items: [
      {
        kicker: '01', name: 'Kick Off', sub: 'Play vs COM',
        desc: 'A single match against the computer. <b>Pick squad size, difficulty, time of day and weather</b> on the next screen.',
        art: '/ui/tiles/kickoff.webp',
        act: () => push(matchSetupScreen()),
      },
      {
        kicker: '02', name: 'Solo Practice', sub: 'Training ground',
        desc: 'An empty pitch, a ball and a goalkeeper. No clock, no opposition — <b>free practice</b>.',
        art: '/ui/tiles/solo.webp',
        act: () => { cb.persist(); cb.onStartSolo(); },
      },
      {
        kicker: '03', name: 'Test Arena', sub: 'Sandbox',
        desc: 'The physics and animation sandbox. <b>Development tool</b>, not a game mode.',
        art: '/ui/tiles/testarena.webp',
        act: () => { cb.persist(); cb.onStartArena(); },
      },
    ],
  });

  /**
   * The trophy cabinet strip.
   *
   * Careers and tournaments get wiped constantly; this is the only thing that
   * survives them, so it is the actual reason to start another one. Shown on
   * the section you enter before every competition, because a shelf you cannot
   * see is a shelf nobody fills.
   */
  const honoursStrip = (): HTMLElement => {
    const h = loadHonours();
    const wrap = el('div', 'am-honours');
    const cell = (value: string, label: string, lit: boolean): void => {
      const c = el('div', `am-hon${lit ? ' is-lit' : ''}`);
      c.appendChild(el('span', 'am-hon-v', value));
      c.appendChild(el('span', 'am-hon-l', label));
      wrap.appendChild(c);
    };
    cell(String(h.worldCups), 'World Cups', h.worldCups > 0);
    cell(String(h.leagueTitles), 'League Titles', h.leagueTitles > 0);
    cell(
      h.bestWorldCupRound >= 0 ? (ROUND_NAMES[h.bestWorldCupRound] ?? '-') : '-',
      'Best WC Run',
      h.bestWorldCupRound >= 0,
    );
    cell(h.bestLeaguePosition > 0 ? ordinalOf(h.bestLeaguePosition) : '-', 'Best Finish', h.bestLeaguePosition === 1);
    cell(String(h.seasonsPlayed), 'Seasons', h.seasonsPlayed > 0);
    return wrap;
  };

  const careerScreen = (): Screen => {
    const career = loadCareer();
    // With the World Cup on its own tab, CAREER goes straight to the thing it
    // is for — no menu whose only job is to offer one real option.
    return career ? careerHubScreen() : clubPickScreen();
  };

  const onlineScreen = (): Screen => ({
    id: 'online',
    tab: 'ONLINE',
    heading: 'Play others',
    kind: 'tiles',
    enterLabel: 'Select',
    items: [
      {
        kicker: '01', name: 'Quick Match', sub: 'Find anyone',
        desc: 'Drop into the next available match against another player.',
        art: '/ui/tiles/quickmatch.webp',
        act: () => withPlayerName((n) => cb.onQuickMatch(n)),
      },
      {
        kicker: '02', name: 'Create Room', sub: 'Invite a friend',
        desc: 'Open a private room and get a code to share.',
        art: '/ui/tiles/createroom.webp',
        act: () => push(createRoomScreen()),
      },
      {
        kicker: '03', name: 'Join with Code', sub: 'Enter a room',
        desc: "Type a friend's room code to join their match.",
        art: '/ui/tiles/joincode.webp',
        act: () => push(joinScreen()),
      },
      {
        kicker: '04', name: 'Ranked Match', sub: 'Rated queue',
        desc: '11v11 is the flagship ranked mode; 5v5 plays too. Win to climb, K=32.',
        act: () => push(rankedMatchScreen()),
      },
      {
        kicker: '05', name: 'Leaderboard', sub: 'Top ratings',
        desc: 'See where the highest-rated players stand.',
        act: () => push(leaderboardScreen()),
      },
    ],
  });

  /**
   * CREATE ROOM — the host sizes the room before it exists (owner, 2026-08-16:
   * "ask the Admin how many players he want the room for").
   *
   * The number is HOW MANY HUMANS A SIDE, not a smaller pitch: the deployed
   * game server is one fixed 11-a-side process, so every match is played on
   * the real pitch with a full XI. This only sets how many of those shirts are
   * open to people; ai-fill drives every seat nobody claims. That is why there
   * is no "bots on/off" here — with one always-on 11v11 server there is no
   * such thing as a match without them, and a toggle that pretended otherwise
   * would be a lie. The copy below says so plainly instead.
   */
  let roomSize: RoomSquadSize = '5v5';
  const ROOM_SIZES: readonly RoomSquadSize[] = ['1v1', '2v2', '3v3', '4v4', '5v5', '7v7', '11v11'];
  const createRoomScreen = (): Screen => ({
    id: 'createroom',
    tab: 'ONLINE',
    heading: 'Create Room',
    kind: 'rows',
    enterLabel: 'Select',
    backLabel: 'Back',
    items: [
      {
        name: 'Room Size', label: 'Room Size',
        // The bot count is spelled out from the size rather than offered as a
        // separate knob: with a fixed 11-a-side server the two are the same
        // number, and showing it is what makes the "how many bots" question
        // answer itself instead of leaving the host to do the arithmetic.
        desc: () => {
          const humans = Number(roomSize.split('v')[0]);
          const bots = 11 - humans;
          return `${humans} real player${humans === 1 ? '' : 's'} a side, `
            + (bots === 0 ? 'no bots — a full human XI.'
              : `${bots} AI teammate${bots === 1 ? '' : 's'} each to complete the XI.`)
            + ' Everyone shares one code and picks a position; the pitch is always full 11-a-side.';
        },
        options: () => ROOM_SIZES.map((m) => ({ label: m, selected: m === roomSize })),
        cycle: (d) => {
          const i = ROOM_SIZES.indexOf(roomSize);
          roomSize = ROOM_SIZES[(i + d + ROOM_SIZES.length) % ROOM_SIZES.length] ?? '5v5';
        },
      },
      {
        name: 'Open the Room',
        desc: 'Creates a private room and gives you a code to share. You are the host: '
          + 'you decide when everyone moves on to picking positions.',
        act: () => withPlayerName((n) => cb.onCreateFriend(n, roomSize)),
      },
    ],
  });

  /**
   * Rated matchmaking (docs/ACCOUNTS-SPEC.md phase 3). 11v11 is the flagship
   * ranked mode — the real pitch, the real roster, exactly what the deployed
   * game server actually simulates. 5v5 is real too, not a smaller pitch: it
   * caps how many of the same 11-a-side roster's slots are open to humans and
   * AI-fills the rest (squad-remap.ts on the lobby translates the position a
   * 5v5 player picked into the matching real slot) — "5v5" here means "5 real
   * opponents, AI teammates round it out," the choice of how many bot-filled
   * seats there are.
   */
  let rankedMode: '11v11' | '5v5' = '11v11';
  const rankedMatchScreen = (): Screen => ({
    id: 'ranked',
    tab: 'ONLINE',
    heading: 'Ranked Match',
    kind: 'rows',
    enterLabel: 'Select',
    backLabel: 'Back',
    items: [
      {
        name: 'Squad Size', label: 'Squad Size',
        desc: '11v11 is the full pitch and XI — the real ranked experience. 5v5 keeps the ' +
          'same real pitch and roster but only needs 5 real opponents a side; AI fills the rest.',
        options: () => (['11v11', '5v5'] as const).map((m) => ({ label: m, selected: m === rankedMode })),
        cycle: () => { rankedMode = rankedMode === '11v11' ? '5v5' : '11v11'; },
      },
      {
        name: 'Find Match',
        desc: 'Join the rated queue for the squad size above. Every account starts at 1000.',
        act: () => withPlayerName((n) => cb.onRankedMatch(n, rankedMode)),
      },
      {
        name: 'Leaderboard',
        desc: 'See where the highest-rated players stand.',
        act: () => push(leaderboardScreen()),
      },
    ],
  });

  /**
   * `null` = not fetched yet, `[]` = fetched and genuinely empty — same
   * loading-vs-empty distinction as accountHistory, so the screen doesn't
   * flash "no players yet" for a moment before real rows land.
   */
  let leaderboard: LeaderboardEntry[] | null = null;
  const refreshLeaderboardIfVisible = (): void => {
    if (stack[stack.length - 1]?.id === 'leaderboard') refreshTop(leaderboardScreen);
  };
  const leaderboardScreen = (): Screen => {
    if (leaderboard === null) {
      void getLeaderboard().then((rows) => {
        leaderboard = rows;
        refreshLeaderboardIfVisible();
      });
    }
    return {
      id: 'leaderboard',
      tab: 'ONLINE',
      heading: 'Leaderboard',
      kind: 'table',
      enterLabel: 'Select',
      backLabel: 'Back',
      items: [],
      panel: () => table([
        { head: true, cells: ['#', 'Player', 'Rating'] },
        ...(leaderboard === null
          ? [{ cells: ['', 'Loading…', ''] }]
          : leaderboard.length === 0
            ? [{ cells: ['', 'No ranked players yet', ''] }]
            : leaderboard.map((p, i) => ({
              highlight: accountProfile !== null && p.username === accountProfile.username,
              cells: [String(i + 1), p.username, String(p.rating)],
            }))),
      ]),
    };
  };

  const joinScreen = (): Screen => ({
    id: 'join',
    tab: 'ONLINE',
    heading: 'Room code',
    kind: 'name',
    enterLabel: 'Join',
    backLabel: 'Back',
    items: [{ name: 'code', desc: 'The code your friend was given when they created the room.' }],
    // Same identity rule as withPlayerName: a signed-in player joins as their
    // username; a guest joins as the last name they used.
    ...({ onConfirm: (v: string) => { joinCode = v; cb.persist(); cb.onJoinCode(accountProfile?.username ?? playerName, joinCode); } } as object),
  });

  /**
   * Sub-screen for typing an account username. Reuses the generic `nameScreen`
   * text field — it pre-fills from `playerName` as a starting suggestion, not
   * because that is the username, and its own desc line is generic enough to
   * apply here too.
   */
  const accountUsernameScreen = (): Screen => nameScreen((v) => {
    const name = sanitiseDisplayName(v) || DEFAULT_DISPLAY_NAME;
    pop();
    flash('Saving profile…');
    void saveAccountProfile(name, avatarDraft).then((res) => {
      if (res.ok) {
        accountProfile = res.profile;
        flash('Profile saved');
      } else if (res.error === 'username-taken') {
        flash('That username is taken');
      } else {
        flash('Could not save profile');
      }
      refreshProfileIfVisible();
    });
  }, 'Account username');

  const profileScreen = (): Screen => {
    const items: Item[] = [
      {
        name: 'Display Name',
        label: 'Display Name',
        desc: 'Shown on the scoreboard and to other players in a room.',
        act: () => push(nameScreen((v) => {
          playerName = sanitiseDisplayName(v) || DEFAULT_DISPLAY_NAME;
          updateProfile({ displayName: playerName });
          renderWho();
          pop();
        }, 'Your name')),
      },
    ];

    if (accountsEnabled()) {
      if (!accountSession) {
        items.push({
          name: 'Continue with Google',
          desc: 'Sign in to save a profile and match history across devices. Playing as a guest still works everywhere.',
          act: () => { void signInWithGoogle(); },
        });
      } else {
        items.push({
          name: `Signed in as ${accountSession.user.email ?? 'Google account'}`,
          desc: 'Your Google account is linked to this browser.',
        });
        items.push({
          name: accountProfile ? `Username: ${accountProfile.username}` : 'Set a username',
          label: 'Username',
          desc: 'A public name (3-16 characters) for your profile and match history.',
          act: () => push(accountUsernameScreen()),
        });
        items.push({
          name: 'Avatar',
          label: 'Avatar',
          desc: 'Pick a preset avatar for your profile.',
          options: () => Array.from({ length: AVATAR_COUNT }, (_, i) => ({
            label: String(i + 1),
            selected: i === avatarDraft,
          })),
          cycle: (d) => {
            avatarDraft = ((avatarDraft + d) % AVATAR_COUNT + AVATAR_COUNT) % AVATAR_COUNT;
            if (accountProfile) {
              void saveAccountProfile(accountProfile.username, avatarDraft).then((res) => {
                if (res.ok) { accountProfile = res.profile; flash('Avatar saved'); }
              });
            }
          },
        });
        // ── Recent matches (ACCOUNTS-SPEC phase 2) ─────────────────────────
        // Written by the GAME SERVER at full time, not by this client, so a
        // match only appears here once it actually finished on the server.
        items.push({
          name: 'Recent Matches',
          desc: 'Matches you finished while signed in. Written by the match server at full time.',
        });
        if (accountHistory === null) {
          items.push({ name: 'Loading…', desc: 'Fetching your match history.' });
        } else if (accountHistory.length === 0) {
          items.push({
            name: 'No matches yet',
            desc: 'Play an online match while signed in and it will show up here.',
          });
        } else {
          for (const m of accountHistory) {
            items.push({
              name: `${RESULT_LABEL[m.result]}  ${m.scored}–${m.conceded}  ·  ${m.mode}`,
              desc: `Played ${formatMatchDate(m.playedAt)}.`,
            });
          }
        }

        items.push({
          name: 'Sign Out',
          desc: 'Sign out of your Google account. You can keep playing as a guest.',
          act: () => { void signOutAccount(); },
        });
      }
    }

    return {
      id: 'profile',
      tab: 'PROFILE',
      heading: 'Profile',
      kind: 'rows',
      enterLabel: 'Select',
      items,
    };
  };


  // ═══ COMPETITIONS ════════════════════════════════════════════════════════
  //
  // Career and the World Cup are PERSISTED (tournament-store.ts, localStorage),
  // so these screens are pure views over that state plus three verbs: start,
  // play the next fixture, simulate it. Every mutation goes back through the
  // store's own functions — none of the league or bracket logic is duplicated
  // here, which is why a season advanced in another tab is simply reloaded.

  const teamName = (id: string): string => teamOrStub(id).name;

  /** Knockout round labels, R16 first. */
  const ROUND_NAMES = ['Round of 16', 'Quarter-final', 'Semi-final', 'Final'];

  /** 1 -> "1st", 2 -> "2nd" ... for the end-of-season line. */
  const ordinalOf = (n: number): string => {
    const r100 = n % 100;
    if (r100 >= 11 && r100 <= 13) return `${n}th`;
    return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
  };

  /** Name of the knockout round a tournament is currently in. */
  const knockoutName = (st: WorldCupState): string =>
    ROUND_NAMES[st.knockoutRound] ?? 'Knockouts';

  /*
   * Ties in one knockout round. The store keeps `bracketRound` private, and a
   * filter over the persisted bracket is the same answer without widening that
   * module's public surface just to draw a view.
   */
  const tiesInRound = (st: WorldCupState, round: number): WorldCupState['bracket'] =>
    st.bracket.filter((m) => m.round === round);

  /**
   * A nation's real flag, or a generated crest for an invented club.
   *
   * NATIONS are real countries with verified flag-icons SVGs served from
   * /ui/flags (teams.ts documents the licence and why they are NOT drawn by
   * hand — an approximated national flag is worse than none). CLUBS are
   * fictional per §16, so there is no flag to show and they get initials on
   * their own kit colours instead.
   */
  const badge = (teamId: string, size = 22): HTMLElement => {
    const t = teamOrStub(teamId);
    if (t.flag) {
      const img = el('img', 'am-flag');
      img.src = `/ui/flags/${t.flag}.svg`;
      img.alt = t.name;
      img.width = Math.round(size * 1.5);
      img.height = size;
      // A missing flag must not leave a broken-image glyph in a bracket.
      img.addEventListener('error', () => { img.style.visibility = 'hidden'; }, { once: true });
      return img;
    }
    const crest = el('span', 'am-crest', t.short);
    crest.style.background = t.primary;
    crest.style.color = t.secondary;
    return crest;
  };

  /** Team badge + name, the unit used in every table row and bracket slot. */
  const teamCell = (teamId: string): HTMLElement => {
    const w = el('span', 'am-team');
    if (teamId) w.appendChild(badge(teamId, 18));
    w.appendChild(el('span', 'am-team-name', teamId ? teamOrStub(teamId).name : 'TBD'));
    return w;
  };

  /**
   * The whole knockout bracket, as columns.
   *
   * Rendered round by round rather than only the live round, because a bracket
   * whose point is "where am I and who is left" has to show the shape of the
   * whole thing. The player's own nation is highlighted in every round it
   * appears, so following your own run is a glance rather than a search.
   */
  const bracketPanel = (state: WorldCupState): HTMLElement => {
    const wrap = el('div', 'am-bracket');
    const rounds = Math.max(1, Math.ceil(Math.log2(Math.max(2, state.bracket.length + 1))));
    for (let r = 0; r < ROUND_NAMES.length; r++) {
      const ties = tiesInRound(state, r);
      if (ties.length === 0) continue;
      const col = el('div', 'am-brcol');
      col.appendChild(el('p', 'am-brcol-head', ROUND_NAMES[r] ?? `Round ${r + 1}`));
      for (const m of ties) {
        const mine = m.home === state.nationId || m.away === state.nationId;
        const live = r === state.knockoutRound && !m.winner;
        const tie = el('div', `am-tie${mine ? ' is-you' : ''}${live ? ' is-live' : ''}`);
        for (const side of ['home', 'away'] as const) {
          const id = m[side];
          const row = el('div', `am-tie-row${m.winner && m.winner === id ? ' is-win' : ''}`);
          row.appendChild(teamCell(id ?? ''));
          const goals = side === 'home' ? m.homeGoals : m.awayGoals;
          const sh = m.shootout ? (side === 'home' ? m.shootout[0] : m.shootout[1]) : null;
          row.appendChild(el('span', 'am-tie-score',
            goals === null ? '' : `${goals}${sh !== null ? ` (${sh})` : ''}`));
          tie.appendChild(row);
        }
        col.appendChild(tie);
      }
      wrap.appendChild(col);
    }
    if (state.champion) {
      const won = el('div', 'am-champ');
      won.appendChild(badge(state.champion, 30));
      won.appendChild(el('span', '', `${teamOrStub(state.champion).name} — Champions`));
      wrap.appendChild(won);
    }
    void rounds;
    return wrap;
  };


  /** MatchSetup for a competition fixture — full 11v11 at the player's settings. */
  const competitionSetup = (competition: CompetitionContext): MatchSetup => ({
    squad: 11,
    difficulty: setup.difficulty,
    halfMinutes: setup.halfMinutes,
    timeOfDay: setup.timeOfDay,
    weather: setup.weather,
    competition,
  });

  const table = (
    rows: { cells: string[]; highlight?: boolean; head?: boolean; teamId?: string }[],
  ): HTMLElement => {
    const t = el('div', 'am-table');
    for (const r of rows) {
      const line = el('div', `am-tr${r.head ? ' is-head' : ''}${r.highlight ? ' is-you' : ''}`);
      r.cells.forEach((c, i) => {
        // Column 1 is the team column: render a badge + name rather than text,
        // so a table and a bracket slot look like the same object.
        if (i === 1 && r.teamId && !r.head) {
          const td = el('span', 'am-td');
          td.appendChild(teamCell(r.teamId));
          line.appendChild(td);
          return;
        }
        line.appendChild(el('span', 'am-td', c));
      });
      t.appendChild(line);
    }
    return t;
  };

  // ── Career ───────────────────────────────────────────────────────────────
  const clubPickScreen = (): Screen => ({
    id: 'career-pick',
    tab: 'CAREER',
    heading: 'Choose your club',
    kind: 'tiles',
    enterLabel: 'Start Career',
    backLabel: 'Back',
    items: CLUBS.map((c, i) => ({
      kicker: String(i + 1).padStart(2, '0'),
      name: c.name,
      badgeTeamId: c.id,
      sub: `${c.subtitle} · ${c.rating}`,
      desc: `Take charge of <b>${c.name}</b> for a full league season. Every other fixture is simulated around you.`,
      act: () => {
        saveCareer(startCareer(c.id));
        replaceRoot(careerHubScreen());
      },
    })),
  });


  /**
   * SEASON END — the league's equivalent of lifting the World Cup.
   *
   * Winning a title used to change a button's description and nothing else,
   * which is a strange way to treat the payoff of thirty-four matches. A title
   * gets the same full-screen treatment, confetti and all; a mid-table finish
   * gets an honest line and a reason to go again.
   */
  const seasonEndScreen = (state: ReturnType<typeof loadCareer> & object): Screen => {
    const rows = careerTable(state);
    const pos = rows.findIndex((r) => r.teamId === state.clubId) + 1;
    const me = rows[pos - 1];
    const won = pos === 1;
    const top4 = pos <= 4;

    /*
     * Banked on a PERSISTED key, not the in-memory token: the menu remounts
     * whenever a match returns to it, and an in-memory guard resets with it.
     * Both calls are idempotent on the same key, so this is safe to run on
     * every render.
     */
    recordSeasonHonours(pos, `career:${state.clubId}:${state.season}`);
    awardSeason(state.clubId, state.season, pos);

    return {
      id: 'career-end',
      tab: 'CAREER',
      heading: '',
      kind: 'moment',
      enterLabel: 'Select',
      backLabel: 'Back',
      moment: {
        tone: won ? 'win' : 'loss',
        kicker: `Season ${state.season} · Arena League`,
        headline: won ? 'Champions' : `${ordinalOf(pos)} place`,
        line: won
          ? `${teamName(state.clubId)} win the league with ${me?.points ?? 0} points.`
          : `${teamName(state.clubId)} finish on ${me?.points ?? 0} points.`,
        note: won
          ? `Title number ${loadHonours().leagueTitles} in the cabinet. Now defend it.`
          : top4
            ? 'Close. A few results either way and that is a title race.'
            : 'A season is thirty-four chances to get it right. Go again.',
        teamId: state.clubId,
      },
      items: [
        {
          name: 'Next Season',
          desc: 'Roll the squad on and start again from matchday one.',
          act: () => { saveCareer(startNextSeason(state)); replaceRoot(careerHubScreen()); },
        },
        {
          name: 'Final Table',
          desc: 'Look back over how the season finished.',
          act: () => push(finalTableScreen(state)),
        },
        {
          name: 'New Club',
          desc: 'Abandon this career and take charge somewhere else.',
          act: () => { clearCareer(); replaceRoot(clubPickScreen()); },
        },
      ],
    };
  };

  const careerHubScreen = (): Screen => {
    const state = loadCareer();
    if (!state) return clubPickScreen();

    const next = nextCareerFixture(state);
    // A completed season is a RESULT, not a hub state.
    if (careerSeasonComplete(state)) return seasonEndScreen(state);
    const rows = careerTable(state);
    const items: Item[] = [];
    // A finished season returned above, so from here `next` is the live fixture.
    if (next) {
      const home = next.home === state.clubId;
      const opp = teamName(home ? next.away : next.home);
      items.push({
        name: 'Play Match',
        desc: `Matchday ${state.round + 1} — <b>${home ? 'home' : 'away'} to ${opp}</b>.`,
        act: () => {
          cb.persist();
          cb.onStartPractice(competitionSetup({
            kind: 'career',
            label: `ARENA LEAGUE · MATCHDAY ${state.round + 1}`,
            teamALabel: teamName(state.clubId),
            teamBLabel: opp,
            token: careerMatchToken(state),
          }));
        },
      });
      /*
       * NO SIMULATE. Removed on the owner's instruction: a fixture you can skip
       * is a fixture that does not matter, and the whole point of a season is
       * that every match is one you played. The REST of the division is still
       * simulated around you when you play yours — that is the store advancing
       * the round, not you skipping it.
       */
    }
    items.push({
      name: 'Abandon Career',
      desc: 'Delete this save and pick a new club. <b>Cannot be undone.</b>',
      act: () => { clearCareer(); replaceRoot(clubPickScreen()); },
    });

    return {
      id: 'career-hub',
      tab: 'CAREER',
      heading: `${teamName(state.clubId)} · Season ${state.season} · Matchday ${Math.min(state.round + 1, careerTotalRounds(state))} of ${careerTotalRounds(state)}`,
      kind: 'table',
      enterLabel: 'Select',
      backLabel: 'Back',
      items,
      panel: () => table([
        { head: true, cells: ['#', 'Club', 'P', 'W', 'D', 'L', 'GD', 'Pts'] },
        ...rows.map((r, i) => ({
          highlight: r.teamId === state.clubId,
          teamId: r.teamId,
          cells: [
            String(i + 1), teamName(r.teamId), String(r.played), String(r.won),
            String(r.drawn), String(r.lost),
            (r.goalDifference > 0 ? '+' : '') + String(r.goalDifference), String(r.points),
          ],
        })),
      ]),
    };
  };

  // ── World Cup ────────────────────────────────────────────────────────────
  const nationPickScreen = (): Screen => ({
    id: 'wc-pick',
    tab: 'WORLD CUP',
    heading: 'Choose your nation',
    kind: 'tiles',
    enterLabel: 'Enter Tournament',
    backLabel: 'Back',
    items: NATIONS.map((n, i) => ({
      kicker: String(i + 1).padStart(2, '0'),
      name: n.name,
      badgeTeamId: n.id,
      sub: `${n.subtitle} · ${n.rating}`,
      desc: `Take <b>${n.name}</b> through the group stage and into the knockouts.`,
      act: () => {
        recordHonours({ tournamentsEntered: loadHonours().tournamentsEntered + 1 });
        saveWorldCup(startWorldCup(n.id));
        replaceRoot(worldCupHubScreen());
      },
    })),
  });


  /**
   * A RESULT MOMENT — the screen you get when a run ends.
   *
   * Losing used to be a disabled list item saying "Knocked Out", which is the
   * least interesting way to end anything. A tournament exit is the emotional
   * peak of the mode and it deserves the whole screen: what happened, how far
   * you got, and — the part that matters — a single obvious way back in.
   *
   * The `note` is not decoration. A loss screen that only restates the loss is
   * a wall; one that points out you went further than last time, or that the
   * side who knocked you out went on to win it, is a reason to press Try Again.
   */
  /**
   * Best knockout round reached BEFORE the current run was banked.
   *
   * knockedOutScreen runs after the honours have been written, so reading the
   * cabinet there would compare this run against itself and never say "furthest
   * you have ever gone". Captured on the way in instead.
   */
  let bestBeforeThisRun = -1;

  const knockedOutScreen = (state: WorldCupState): Screen => {
    let reached = -1;
    for (const m of state.bracket) {
      if (m.home === state.nationId || m.away === state.nationId) reached = Math.max(reached, m.round);
    }
    const roundName = ROUND_NAMES[reached] ?? 'group stage';
    const beat = state.bracket.find(
      (m) => m.round === reached && (m.home === state.nationId || m.away === state.nationId),
    );
    const conqueror = beat && beat.winner && beat.winner !== state.nationId ? beat.winner : null;
    const better = reached > bestBeforeThisRun;

    return {
      id: 'wc-out',
      tab: 'WORLD CUP',
      heading: '',
      kind: 'moment',
      enterLabel: 'Select',
      backLabel: 'Back',
      moment: {
        tone: 'loss',
        kicker: 'World Cup',
        headline: 'Your run ends here',
        line: `${teamName(state.nationId)} go out at the ${roundName}.`,
        note: better
          ? 'That is the furthest you have ever gone. Go again and go further.'
          : conqueror
            ? `Beaten by ${teamName(conqueror)} — watch them and take it out on someone next time.`
            : 'Every champion has been knocked out before. Go again.',
        teamId: state.nationId,
      },
      items: [
        {
          name: 'Try Again',
          desc: `Enter a new tournament with <b>${teamName(state.nationId)}</b> straight away.`,
          act: () => {
            recordHonours({ tournamentsEntered: loadHonours().tournamentsEntered + 1 });
            saveWorldCup(startWorldCup(state.nationId));
            replaceRoot(worldCupHubScreen());
          },
        },
        {
          name: 'Different Nation',
          desc: 'Pick someone else and start again.',
          act: () => { clearWorldCup(); replaceRoot(nationPickScreen()); },
        },
        {
          name: 'See the Rest',
          desc: 'Play the remaining rounds out and find out who lifts it.',
          act: () => {
            let n = state;
            for (let g = 0; g < 8 && worldCupStage(n) !== 'done'; g++) n = advanceWorldCupRound(n, null);
            saveWorldCup(n);
            // Straight to the bracket VIEW. Routing back through the hub would
            // hand an eliminated player their own knocked-out screen again.
            push(bracketScreen(n));
          },
        },
      ],
    };
  };

  const championScreen = (state: WorldCupState): Screen => ({
    id: 'wc-win',
    tab: 'WORLD CUP',
    heading: '',
    kind: 'moment',
    enterLabel: 'Select',
    backLabel: 'Back',
    moment: {
      tone: 'win',
      kicker: 'World Cup Final',
      headline: 'Champions of the world',
      line: `${teamName(state.nationId)} lift the trophy.`,
      note: `That is World Cup number ${loadHonours().worldCups} in the cabinet.`,
      teamId: state.nationId,
    },
    items: [
      {
        name: 'Defend It',
        desc: 'Enter the next tournament with the same nation.',
        act: () => {
          recordHonours({ tournamentsEntered: loadHonours().tournamentsEntered + 1 });
          saveWorldCup(startWorldCup(state.nationId));
          replaceRoot(worldCupHubScreen());
        },
      },
      {
        name: 'View Bracket',
        desc: 'Look back over how the tournament went.',
        act: () => push(worldCupHubScreen()),
      },
      {
        name: 'Trophy Cabinet',
        desc: 'Everything you have won.',
        // The cabinet lives on the picker screens, which is where it is read
        // before choosing what to enter next.
        act: () => push(nationPickScreen()),
      },
    ],
  });


  /**
   * The finished season's table, as a plain view.
   *
   * `careerHubScreen()` early-returns the season-end MOMENT once a season is
   * complete, so "Final Table" pushing the hub just re-pushed the moment and
   * the screen appeared not to respond at all. A view the player asked for
   * needs its own screen rather than a route through a hub that has since
   * changed its mind about what it shows.
   */
  const finalTableScreen = (state: NonNullable<ReturnType<typeof loadCareer>>): Screen => {
    const rows = careerTable(state);
    return {
      id: 'career-table',
      tab: 'CAREER',
      heading: `Season ${state.season} — final table`,
      kind: 'table',
      enterLabel: 'Select',
      backLabel: 'Back',
      items: [
        {
          name: 'Next Season',
          desc: 'Roll the squad on and start again from matchday one.',
          act: () => { saveCareer(startNextSeason(state)); replaceRoot(careerHubScreen()); },
        },
        { name: 'Back', desc: 'Return to the season result.', act: () => pop() },
      ],
      panel: () => table([
        { head: true, cells: ['#', 'Club', 'P', 'W', 'D', 'L', 'GD', 'Pts'] },
        ...rows.map((r, i) => ({
          highlight: r.teamId === state.clubId,
          teamId: r.teamId,
          cells: [
            String(i + 1), teamName(r.teamId), String(r.played), String(r.won),
            String(r.drawn), String(r.lost),
            (r.goalDifference > 0 ? '+' : '') + String(r.goalDifference), String(r.points),
          ],
        })),
      ]),
    };
  };

  /**
   * The bracket, as a plain view.
   *
   * Same reason as the table: after being knocked out, "See the Rest" routed
   * through the hub, which — still seeing an eliminated player — returned the
   * knocked-out moment again. The button did its work and appeared to do
   * nothing.
   */
  const bracketScreen = (state: WorldCupState): Screen => ({
    id: 'wc-bracket',
    tab: 'WORLD CUP',
    heading: state.champion
      ? `${teamName(state.champion)} — champions`
      : `${teamName(state.nationId)} — bracket`,
    kind: 'table',
    enterLabel: 'Select',
    backLabel: 'Back',
    items: [
      {
        name: 'New Tournament',
        desc: `Enter again with <b>${teamName(state.nationId)}</b>.`,
        act: () => {
          recordHonours({ tournamentsEntered: loadHonours().tournamentsEntered + 1 });
          saveWorldCup(startWorldCup(state.nationId));
          replaceRoot(worldCupHubScreen());
        },
      },
      { name: 'Back', desc: 'Return to the result.', act: () => pop() },
    ],
    panel: () => bracketPanel(state),
  });

  const worldCupHubScreen = (): Screen => {
    const state = loadWorldCup();
    if (!state) return nationPickScreen();

    const stage = worldCupStage(state);
    const out = worldCupEliminated(state);

    /*
     * Pay for reaching this knockout round, and for the trophy. Awarded on
     * arrival rather than on winning, so a run that ends in the quarter-finals
     * is still paid for getting there — same principle as paying for a defeat.
     * Keyed on seed + round, so re-rendering the hub cannot mint coins.
     */
    if (stage === 'knockout' || stage === 'done') {
      const reached = state.bracket
        .filter((m) => m.home === state.nationId || m.away === state.nationId)
        .reduce((hi, m) => Math.max(hi, m.round), -1);
      if (reached >= 0) {
        awardKnockout(state.seed, reached, state.champion === state.nationId);
      }
    }

    /*
     * Bank the result the first time we see a finished tournament, then show
     * the moment. `bankedToken` guards against re-counting: this screen is
     * rebuilt on every render, and without it a champion would earn a trophy
     * per repaint.
     */
    if (stage === 'done') {
      recordWorldCupHonours(state);
      if (state.champion === state.nationId) return championScreen(state);
    }
    if (out && stage !== 'done') {
      /*
       * Bank the run on ELIMINATION, not only on completion.
       *
       * recordWorldCupHonours fires when a tournament reaches 'done', but being
       * knocked out in the quarter-finals does not end the tournament — it ends
       * YOURS. Without this the cabinet stayed empty for every run the player
       * did not win, which is nearly all of them, and a shelf that only fills
       * on a trophy gives a beaten player nothing to come back for.
       */
      // recordWorldCupHonours is keyed on the tournament, so this is safe on
      // every render; capture the previous best BEFORE it banks.
      bestBeforeThisRun = loadHonours().bestWorldCupRound;
      recordWorldCupHonours(state);
      return knockedOutScreen(state);
    }
    if (stage === 'done' && state.champion === state.nationId) return championScreen(state);
    const next = nextWorldCupMatch(state);
    const items: Item[] = [];

    if (next && !out) {
      const oppId = next.kind === 'group'
        ? (next.fixture.home === state.nationId ? next.fixture.away : next.fixture.home)
        : (next.match.home === state.nationId ? next.match.away : next.match.home);
      const label = next.kind === 'group'
        ? `WORLD CUP · GROUP ${groupOf(state, state.nationId)?.name ?? ''}`
        : `WORLD CUP · ${knockoutName(state)}`;
      items.push({
        name: 'Play Match',
        desc: `${next.kind === 'group' ? 'Group match' : knockoutName(state)} against <b>${teamName(oppId ?? '')}</b>.`,
        act: () => {
          cb.persist();
          cb.onStartPractice(competitionSetup({
            kind: 'worldCup',
            label,
            teamALabel: teamName(state.nationId),
            teamBLabel: teamName(oppId ?? ''),
            token: next.token,
          }));
        },
      });
      // No simulate here either — see the career hub.
    } else if (out) {
      /*
       * Knocked out, but the tournament is not finished.
       *
       * Without this the bracket is a dead end: no fixture is yours, so there
       * is nothing to play and nothing to simulate, and the only remaining verb
       * is Abandon — you cannot even see who wins the thing you were just
       * knocked out of. Letting the rest be played through is the whole payoff
       * of having a bracket on screen.
       */
      if (stage !== 'done') {
        items.push({
          name: 'Play It Out',
          desc: 'You are out — simulate the remaining rounds and see who lifts it.',
          act: () => {
            let next2 = state;
            // Run to the end rather than one round at a time: once you are out,
            // clicking through rounds you have no stake in is just friction.
            for (let guard = 0; guard < 8 && worldCupStage(next2) !== 'done'; guard++) {
              next2 = advanceWorldCupRound(next2, null);
            }
            saveWorldCup(next2);
            push(bracketScreen(next2));
          },
        });
      }
      items.push({
        name: state.champion ? 'Champions' : 'Knocked Out',
        disabled: true,
        desc: state.champion
          ? `<b>${teamName(state.champion)}</b> lifted the trophy.`
          : 'Your run is over. Play it out to see who wins, or start again.',
      });
    }
    items.push({
      name: 'Abandon Tournament',
      desc: 'Delete this tournament and pick a new nation. <b>Cannot be undone.</b>',
      act: () => { clearWorldCup(); replaceRoot(nationPickScreen()); },
    });

    const panel = (): HTMLElement => {
      if (stage === 'groups') {
        const g = groupOf(state, state.nationId);
        if (!g) return el('div');
        const rows = groupStandings(state, g);
        return table([
          { head: true, cells: ['#', `Group ${g.name}`, 'P', 'W', 'D', 'L', 'GD', 'Pts'] },
          ...rows.map((r, i) => ({
            highlight: r.teamId === state.nationId,
            teamId: r.teamId,
            cells: [
              String(i + 1), teamName(r.teamId), String(r.played), String(r.won),
              String(r.drawn), String(r.lost),
              (r.goalDifference > 0 ? '+' : '') + String(r.goalDifference), String(r.points),
            ],
          })),
        ]);
      }
      return bracketPanel(state);
    };

    return {
      id: 'wc-hub',
      tab: 'WORLD CUP',
      heading: `${teamName(state.nationId)} · ${stage === 'groups' ? 'Group Stage' : stage === 'done' ? 'Complete' : knockoutName(state)}`,
      kind: 'table',
      enterLabel: 'Select',
      backLabel: 'Back',
      items,
      panel,
    };
  };


  // ═══ STORE ═══════════════════════════════════════════════════════════════
  //
  // Celebrations, priced in coins AND cash (economy/catalogue.ts explains why
  // every item must be buyable both ways). Ownership is asked of the
  // entitlement store, never read from storage here — that indirection is what
  // lets the authority move to a server without touching this screen.

  const RARITY_LABEL: Record<CatalogueItem['rarity'], string> = {
    common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary',
  };

  /** One catalogue row: what it is, what it costs, and what you can do about it. */
  const storeRow = (item: CatalogueItem): Item => {
    const store = entitlements();
    const owned = item.kind !== 'coins' && store.owns(item.id);
    const equipped = owned && equippedCelebration() === item.id;
    const afford = item.coinPrice !== null && balance() >= item.coinPrice;

    /*
     * The action a row offers depends on where the player stands with it:
     * equipped -> nothing to do, owned -> equip, affordable -> buy,
     * otherwise -> say plainly how short they are. A row that always says BUY
     * and then fails is how a store loses trust.
     */
    let verb: string;
    let act: (() => void) | undefined;
    if (item.kind === 'coins') {
      verb = paymentAvailable() ? `Buy ${formatCash(item.cashPriceMinor ?? 0)}` : 'Coming soon';
      act = paymentAvailable()
        ? () => { void store.buyWithCash(item).then(() => refreshTop(storeScreen)); }
        : undefined;
    } else if (equipped) {
      verb = 'Equipped';
    } else if (owned) {
      verb = 'Equip';
      act = () => { equipCelebration(item.id); refreshTop(storeScreen); };
    } else if (afford) {
      verb = `Buy ${formatCoins(item.coinPrice ?? 0)}`;
      act = () => {
        const res = store.buyWithCoins(item);
        if (res.ok) {
          // Equip immediately on purchase. Nobody buys a celebration and then
          // wants to hunt for a second button to actually use it.
          equipCelebration(item.id);
          flash(`${item.name} unlocked`);
        } else {
          flash(res.reason === 'insufficient-coins' ? 'Not enough coins' : 'Could not buy that');
        }
        refreshTop(storeScreen);
      };
    } else {
      const short = (item.coinPrice ?? 0) - balance();
      verb = `${formatCoins(short)} short`;
    }

    const cash = item.cashPriceMinor !== null ? ` · ${formatCash(item.cashPriceMinor)}` : '';
    return {
      name: item.name,
      label: verb,
      desc: owned
        ? `<b>${item.name}</b> — ${item.blurb} You own this.`
        : `<b>${item.name}</b> — ${item.blurb} ${
            item.coinPrice !== null ? `${formatCoins(item.coinPrice)} coins${cash}.` : `${cash.slice(3)}.`
          }`,
      disabled: act === undefined,
      ...(act ? { act } : {}),
      storeItem: item,
      storeState: equipped ? 'equipped' : owned ? 'owned' : afford ? 'afford' : 'short',
    };
  };

  const storeScreen = (): Screen => ({
    id: 'store',
    tab: 'STORE',
    heading: 'Animations',
    kind: 'store',
    enterLabel: 'Select',
    items: [
      /*
       * Every sellable kind, explicitly. Listing only 'celebration' silently
       * hid both Boost animations from the store — an item in the catalogue
       * that no screen lists is an item nobody can buy, and nothing warns you.
       */
      ...itemsOfKind('boost').map(storeRow),
      ...itemsOfKind('celebration').map(storeRow),
      ...itemsOfKind('kit').map(storeRow),
      ...itemsOfKind('chant').map(storeRow),
      ...itemsOfKind('coins').map(storeRow),
    ],
  });

  const screenForTab = (tab: string): Screen => {
    switch (tab) {
      case 'CAREER': return careerScreen();
      case 'WORLD CUP': {
        const wc = loadWorldCup();
        return wc ? worldCupHubScreen() : nationPickScreen();
      }
      case 'STORE': return storeScreen();
      case 'ONLINE': return onlineScreen();
      case 'PROFILE': return profileScreen();
      default: return playScreen();
    }
  };

  // ── Rendering ────────────────────────────────────────────────────────────
  function renderWho(): void {
    who.innerHTML = '';
    /*
     * The balance sits in the rail on EVERY screen. A currency you have to go
     * and look for is a currency nobody notices they are earning, and the whole
     * point of paying out for matches is that the player sees the number move.
     */
    const purse = el('span', 'am-purse');
    purse.appendChild(el('span', 'am-coin', ''));
    purse.appendChild(el('span', 'am-purse-v', formatCoins(balance())));
    const dot = el('span', 'am-who-dot');
    dot.style.background = kitA;
    const label = el('span');
    label.innerHTML = `<b>${playerName.replace(/[<>&]/g, '')}</b>`;
    who.append(purse, dot, label);

    // Live "who's around" readout — omitted entirely until the first /stats
    // response lands, rather than flashing a wrong "0" while it's in flight.
    if (activePlayers !== null) {
      const online = el('span', 'am-online');
      const pulse = el('span', 'am-online-dot');
      online.append(pulse, el('span', 'am-online-n', String(activePlayers)));
      who.prepend(online);
    }
  }

  function renderTabs(active: string): void {
    tabsEl.innerHTML = '';
    for (const t of TABS) {
      const n = el('div', `am-tab${t === active ? ' is-on' : ''}`, t);
      n.addEventListener('click', () => {
        if (t === 'SETTINGS') { cb.onOpenSettings(); return; }
        replaceRoot(screenForTab(t));
      });
      tabsEl.appendChild(n);
    }
  }

  function renderPrompts(s: Screen): void {
    prompts.innerHTML = '';
    const add = (key: string, verb: string): void => {
      const w = el('div');
      w.innerHTML = `<span class="am-key">${key}</span>${verb}`;
      prompts.appendChild(w);
    };
    add(s.kind === 'rows' ? '↑↓' : '←→', 'Navigate');
    if (s.kind === 'rows') add('←→', 'Change');
    add('Enter', s.enterLabel ?? 'Select');
    if (s.backLabel) add('Esc', s.backLabel);
    add('Tab', 'Sections');
    // A purchase confirmation belongs where the player is already looking for
    // feedback, not in a modal that has to be dismissed.
    statusEl.textContent = performance.now() < flashUntil ? flashText : '';
    prompts.appendChild(statusEl);
  }

  function render(): void {
    if (disposed) return;
    const s = stack[stack.length - 1];
    if (!s) return;

    renderTabs(s.tab);
    renderWho();
    // Drives the background-art layer (arena-menu.css's `.am-art` rules) —
    // see its construction comment above for why this is a data attribute
    // rather than a second tracked value.
    root.dataset['tab'] = s.tab;
    headingEl.textContent = s.heading;
    contentEl.innerHTML = '';
    contentEl.className = '';

    if (s.kind === 'tiles') {
      /*
       * The CAREER section stacks: modes on top, trophy cabinet underneath. Every
       * other tile screen is a plain row.
       */
      const stacked = s.id === 'career-pick' || s.id === 'wc-pick';
      contentEl.className = stacked ? 'am-stack' : 'am-tiles';
      const tileRow = stacked ? el('div', 'am-tiles') : contentEl;
      if (stacked) contentEl.appendChild(tileRow);
      s.items.forEach((it, i) => {
        const t = el('div', `am-tile${focusVisible(i) ? ' is-focus' : ''}`);
        if (it.disabled) t.dataset['disabled'] = '1';
        // Photo tile (PLAY's three modes). Three layers, bottom to top: the
        // photo itself (cover, anchored right — every one of these images is
        // composed with its subject on the right, same as the section art);
        // the tile's own existing glass tint, kept so a photo tile still
        // reads as the SAME card component and not a special one; a bottom-
        // heavy scrim so the kicker/name/sub text — which sits at the card's
        // bottom edge — stays legible regardless of what is under it.
        if (it.art) {
          t.style.backgroundImage =
            `linear-gradient(180deg, rgba(7,11,18,0.05) 0%, rgba(7,11,18,0.38) 46%, rgba(7,11,18,0.95) 100%), ` +
            `linear-gradient(160deg, var(--am-tile-hi), var(--am-tile)), ` +
            `url('${it.art}')`;
          t.style.backgroundSize = 'auto, auto, cover';
          t.style.backgroundPosition = '0 0, 0 0, right center';
          t.style.backgroundRepeat = 'no-repeat, no-repeat, no-repeat';
        }
        if (it.badgeTeamId) {
          const b = el('div', 'am-tile-badge');
          b.appendChild(badge(it.badgeTeamId, 30));
          t.appendChild(b);
        }
        if (it.kicker) t.appendChild(el('span', 'am-tile-num', it.kicker));
        t.appendChild(el('span', 'am-tile-name', it.name));
        if (it.sub) t.appendChild(el('span', 'am-tile-sub', it.sub));
        // Hover writes the SAME focus the keyboard uses, so the two can never
        // disagree about what is selected.
        t.addEventListener('mouseenter', () => { setFocus(i); });
        // Set focus FROM THE CLICKED INDEX before acting. activate() reads
        // `focus`, and a click is not always preceded by a hover — a touch tap
        // has none at all, so a tap used to fire whatever the keyboard had
        // selected rather than the thing under the finger.
        t.addEventListener('click', () => { focus = i; activate(); });
        tileRow.appendChild(t);
      });
      if (stacked) contentEl.appendChild(honoursStrip());
    } else if (s.kind === 'rows') {
      contentEl.className = 'am-rows';
      s.items.forEach((it, i) => {
        const on = focusVisible(i);
        if (!it.options) {
          const cta = el('div', `am-cta${on ? ' is-focus' : ''}`, it.name);
          cta.addEventListener('mouseenter', () => { setFocus(i); });
          cta.addEventListener('click', () => { focus = i; activate(); });
          contentEl.appendChild(cta);
          return;
        }
        const row = el('div', `am-row${on ? ' is-focus' : ''}`);
        row.appendChild(el('span', 'am-row-label', it.label ?? it.name));
        const opts = el('div', 'am-row-opts');
        for (const o of it.options()) {
          opts.appendChild(el('span', `am-opt${o.selected ? ' is-sel' : ''}`, o.label));
        }
        row.append(opts);
        row.addEventListener('mouseenter', () => { setFocus(i); });
        row.addEventListener('click', () => { focus = i; it.cycle?.(1); render(); });
        contentEl.appendChild(row);
      });
    } else if (s.kind === 'moment' && s.moment) {
      const m = s.moment;
      contentEl.className = `am-moment is-${m.tone}`;
      const card = el('div', 'am-moment-card');
      if (m.teamId) {
        const b = el('div', 'am-moment-badge');
        b.appendChild(badge(m.teamId, 46));
        card.appendChild(b);
      }
      card.appendChild(el('p', 'am-moment-kicker', m.kicker));
      card.appendChild(el('h2', 'am-moment-head', m.headline));
      card.appendChild(el('p', 'am-moment-line', m.line));
      card.appendChild(el('p', 'am-moment-note', m.note));
      /*
       * A win gets falling confetti in the menu itself. The ON-PITCH
       * celebration after a final is a separate, bigger job (render/
       * celebration.ts already fires confetti and night fireworks during a
       * match) — this is the front-end half, and the hook for the pitch half is
       * the `tone === 'win'` branch here.
       */
      if (m.tone === 'win') {
        const sky = el('div', 'am-confetti');
        for (let i = 0; i < 60; i++) {
          const f = el('i');
          f.style.left = `${(i * 37) % 100}%`;
          f.style.animationDelay = `${(i % 20) * 0.18}s`;
          f.style.animationDuration = `${3.2 + ((i * 7) % 25) / 10}s`;
          f.style.background = i % 3 === 0 ? '#ffd94a' : i % 3 === 1 ? '#4ade80' : '#eef3fa';
          sky.appendChild(f);
        }
        contentEl.appendChild(sky);
      }
      const actions = el('div', 'am-moment-actions');
      s.items.forEach((it, i) => {
        const btn = el('div', `am-action${focusVisible(i) ? ' is-focus' : ''}`, it.name);
        btn.addEventListener('mouseenter', () => { setFocus(i); });
        btn.addEventListener('click', () => { focus = i; activate(); });
        actions.appendChild(btn);
      });
      card.appendChild(actions);
      contentEl.appendChild(card);
    } else if (s.kind === 'store') {
      contentEl.className = 'am-store';
      s.items.forEach((it, i) => {
        const item = it.storeItem;
        if (!item) return;
        const card = el('div', `am-sku is-${item.rarity}${focusVisible(i) ? ' is-focus' : ''}`);
        card.appendChild(el('span', 'am-sku-rarity', RARITY_LABEL[item.rarity]));
        card.appendChild(el('span', 'am-sku-name', item.name));
        card.appendChild(el('span', 'am-sku-blurb', item.blurb));
        const cta = el('span', `am-sku-cta is-${it.storeState ?? 'short'}`, it.label ?? '');
        card.appendChild(cta);
        card.addEventListener('mouseenter', () => { setFocus(i); });
        card.addEventListener('click', () => { focus = i; activate(); });
        contentEl.appendChild(card);
      });
    } else if (s.kind === 'table') {
      contentEl.className = 'am-split';
      const panelWrap = el('div', 'am-panel');
      if (s.panel) panelWrap.appendChild(s.panel());
      const actions = el('div', 'am-actions');
      s.items.forEach((it, i) => {
        const b = el('div', `am-action${focusVisible(i) ? ' is-focus' : ''}${it.disabled ? ' is-off' : ''}`, it.name);
        b.addEventListener('mouseenter', () => { setFocus(i); });
        b.addEventListener('click', () => { focus = i; activate(); });
        actions.appendChild(b);
      });
      contentEl.append(panelWrap, actions);
    } else {
      const input = el('input', 'am-field');
      input.type = 'text';
      input.maxLength = s.id === 'join' ? 12 : 16;
      input.value = s.id === 'join' ? joinCode : playerName;
      input.placeholder = s.id === 'join' ? 'ROOM CODE' : 'YOUR NAME';
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (s as unknown as { onConfirm?(v: string): void }).onConfirm?.(input.value);
        }
        // Let Esc bubble to the global handler so Back still works from a field.
        e.stopPropagation();
        if (e.key === 'Escape') pop();
      });
      contentEl.appendChild(input);
      // Focus after paint so the caret lands without the browser scrolling.
      requestAnimationFrame(() => input.focus());
    }

    descEl.innerHTML = descOf(s.items[focus]);
    renderPrompts(s);

    /*
     * Keep the focused item ON SCREEN. With 32 nations the picker scrolls, and
     * arrow-keying past the fold would otherwise move an invisible highlight —
     * the keyboard would work perfectly and look completely broken.
     */
    const live = contentEl.querySelector('.is-focus');
    live?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * Every focusable node on the current screen, in ITEM ORDER.
   *
   * The selectors are listed so that document order matches `items` order on
   * every screen kind — rows and their CTA, store cards, moment actions — which
   * is what lets `setFocus(i)` address the right node without each renderer
   * having to hand back a list.
   */
  const focusNodes = (): HTMLElement[] =>
    [...contentEl.querySelectorAll<HTMLElement>('.am-tile, .am-row, .am-cta, .am-action, .am-sku')];

  /**
   * Move the highlight WITHOUT re-rendering.
   *
   * This exists because hover used to call render(), and render() wipes
   * contentEl and rebuilds it. A real mouse click is mousemove -> mousedown ->
   * mouseup -> click, so the mousemove fired mouseenter, which destroyed the
   * very element the click was about to land on — and the click never happened.
   * Every button in the menu was dead to an actual mouse while working
   * perfectly under a synthetic element.click(), which is exactly the class of
   * bug a programmatic test cannot see.
   *
   * Toggling a class is also simply the right amount of work for moving a
   * cursor; rebuilding a screen to highlight a different row was never
   * necessary.
   */
  // `source` defaults to 'mouse' because 6 of this function's 7 call sites
  // ARE a mouseenter handler; `move()` below is the one keyboard exception
  // and passes 'keyboard' explicitly.
  const setFocus = (i: number, source: 'keyboard' | 'mouse' = 'mouse'): void => {
    const s2 = stack[stack.length - 1];
    if (!s2) return;
    const n = s2.items.length;
    if (n === 0) return;
    focus = ((i % n) + n) % n;
    focusSource = source;
    if (source === 'mouse') mouseInsideGrid = true;
    const nodes = focusNodes();
    nodes.forEach((node, k) => node.classList.toggle('is-focus', focusVisible(k)));
    descEl.innerHTML = descOf(s2.items[focus]);
    nodes[focus]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  /*
   * The other half of the fix: leaving the grid entirely (not just moving
   * between two adjacent tiles, which never fires this — mouseleave on a
   * PARENT only fires when the pointer leaves the parent's own bounds) drops
   * `mouseInsideGrid`, and — only if the CURRENT highlight is mouse-driven,
   * so an arrow-key selection is left alone — re-applies `focusVisible` to
   * every node, which now reads false across the board and clears the glow.
   * Attached once, outside render(): `contentEl` itself is created once at
   * mount and only its children are rebuilt each render, so one listener
   * here already covers every screen kind that ever populates it.
   */
  contentEl.addEventListener('mouseleave', () => {
    mouseInsideGrid = false;
    if (focusSource !== 'mouse') return;
    focusNodes().forEach((node, k) => node.classList.toggle('is-focus', focusVisible(k)));
    descEl.innerHTML = '';
  });

  // ── Input ────────────────────────────────────────────────────────────────
  function move(d: -1 | 1): void {
    const s = stack[stack.length - 1];
    if (!s || s.kind === 'name') return;
    const n = s.items.length;
    for (let k = 1; k <= n; k++) {
      const cand = (focus + d * k + n * k) % n;
      if (!s.items[cand]?.disabled) { setFocus(cand, 'keyboard'); break; }
    }
  }

  function activate(): void {
    const s = stack[stack.length - 1];
    const it = s?.items[focus];
    if (!it || it.disabled) return;
    if (it.act) { it.act(); return; }
    // An option row with no `act` toggles forward — Enter on a setting should
    // do something rather than nothing.
    it.cycle?.(1);
    render();
  }

  const onKey = (e: KeyboardEvent): void => {
    if (disposed) return;
    const s = stack[stack.length - 1];
    if (!s) return;
    switch (e.key) {
      case 'ArrowRight':
        if (s.kind === 'rows') { s.items[focus]?.cycle?.(1); render(); } else move(1);
        e.preventDefault();
        break;
      case 'ArrowLeft':
        if (s.kind === 'rows') { s.items[focus]?.cycle?.(-1); render(); } else move(-1);
        e.preventDefault();
        break;
      case 'ArrowDown': move(1); e.preventDefault(); break;
      case 'ArrowUp': move(-1); e.preventDefault(); break;
      case 'Enter': activate(); e.preventDefault(); break;
      case 'Escape': pop(); e.preventDefault(); break;
      case 'Tab': {
        // Tab cycles the top sections, which is the fastest way around and the
        // one shortcut a keyboard player expects to exist.
        const cur = stack[stack.length - 1]?.tab ?? 'PLAY';
        const i = Math.max(0, CYCLE_TABS.indexOf(cur as (typeof CYCLE_TABS)[number]));
        const next = CYCLE_TABS[(i + (e.shiftKey ? -1 : 1) + CYCLE_TABS.length) % CYCLE_TABS.length]!;
        replaceRoot(screenForTab(next));
        e.preventDefault();
        break;
      }
      default: break;
    }
  };
  window.addEventListener('keydown', onKey);

  // ── Boot ─────────────────────────────────────────────────────────────────
  const initial: Record<string, () => Screen> = {
    career: careerScreen,
    // main.ts sends 'worldcup' when a tournament match exits back to the menu,
    // so it must land on the tournament rather than the league.
    worldcup: () => (loadWorldCup() ? worldCupHubScreen() : nationPickScreen()),
    online: onlineScreen,
    profile: profileScreen, bots: matchSetupScreen, practice: matchSetupScreen,
  };
  replaceRoot((initial[startAt] ?? playScreen)());

  // Fires immediately with the current session (see account.ts), so this
  // also does the initial profile load — no separate boot-time fetch needed.
  const stopAuthWatch = onAuthChange((session) => {
    accountSession = session;
    if (!session) {
      accountProfile = null;
      // Back to "not fetched", not "empty": the next sign-in must show a
      // loading row rather than the previous account's emptiness.
      accountHistory = null;
      refreshProfileIfVisible();
      return;
    }
    const loading = getAccountProfile().then((p) => {
      accountProfile = p;
      if (p) avatarDraft = p.avatar;
      refreshProfileIfVisible();
      return p;
    });
    accountProfileLoading = loading;
    void loading.finally(() => { if (accountProfileLoading === loading) accountProfileLoading = null; });
    // Independent of the profile fetch: a player with no profile row yet can
    // still have finished matches, and either result should paint as it lands.
    void getMatchHistory().then((h) => {
      accountHistory = h;
      refreshProfileIfVisible();
    });
  });

  // Live "who's around" readout (rail, every screen). A plain interval, not
  // tied to any screen/auth event — it's genuinely independent live state,
  // and the lobby's own /stats route already sweeps its own staleness, so
  // there is nothing here to invalidate on navigation.
  const refreshActivePlayers = (): void => {
    void fetch(`${lobbyUrl().replace(/\/+$/, '')}/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: unknown) => {
        if (disposed) return;
        const n = (data as { activePlayers?: unknown } | null)?.activePlayers;
        if (typeof n !== 'number') return;
        activePlayers = n;
        renderWho();
      })
      .catch(() => { /* transient — next tick retries */ });
  };
  refreshActivePlayers();
  const activePlayersTimer = window.setInterval(refreshActivePlayers, 20_000);

  return {
    setStatus(text: string): void { statusEl.textContent = text; },
    setBusy(busy: boolean): void { root.dataset['busy'] = busy ? '1' : '0'; },
    dispose(): void {
      disposed = true;
      window.removeEventListener('keydown', onKey);
      window.clearInterval(activePlayersTimer);
      stopAuthWatch();
      root.remove();
    },
  };
}
